import { BotSequenceType } from "./BotSequence.Enums";
import { BOT_SEQUENCE_FMS_NOTE_DEFAULT_TEXT } from "./BotSequenceConfig.Interfaces";

/**
 * The default text every sequence field starts with (ENG-3216 Round 2, decisions 51–53).
 *
 * The day is dynamic, so there is no copy per day: each type has a **Day 0** text (the anchor
 * day reads differently — due today, moved in today, moving out today, started a reservation
 * today) and one **after Day 0** text that reads the day from a variable
 * (`{{Lease:DaysPastDue}}` for Past Due, `{{Sequence:Day}}` for the others). Guidance is one
 * line per type — the assistant is told which day it is.
 *
 * Text and voicemail copy is the legacy Cortex default copy moved onto the new variables;
 * Move In / Move Out voicemail, every email and every guidance line are new (reviewed by Chuck
 * in the plan doc, 2026-09-21). The Studio prefills a field from here the moment it is turned
 * on; the engine sends what is stored and never falls back to this table.
 */
export interface BotSequenceCopyDefaults {
  /** Operator guidance appended to the type prompt when a text or call is "Written by the assistant". */
  guidance: string;
  /** Exact-wording text message (English). */
  sms: string;
  /** Exact-wording voicemail, spoken after the beep. */
  voicemail: string;
  emailSubject: string;
  emailBody: string;
  fmsNote: string;
}

const SIGN_OFF = "{{Location:CortexFacilityName}} · {{Location:CortexFacilityPhone}}";

interface TypeCopy {
  guidance: string;
  day0: Omit<BotSequenceCopyDefaults, "guidance" | "fmsNote">;
  later: Omit<BotSequenceCopyDefaults, "guidance" | "fmsNote">;
}

const COPY: Record<BotSequenceType, TypeCopy> = {
  [BotSequenceType.PAST_DUE]: {
    guidance:
      "The tenant's rent is past due; you know how many days and the balance. Be warm, brief and clear: state the balance, offer the online payment link and the phone number, and offer help if something has come up. Firmer the longer it has been unpaid, never threatening.",
    day0: {
      sms: "{{Location:CortexFacilityName}}: We wanted to bring to your attention that your self storage unit payment is currently past due. As of today, your outstanding balance is ${{Lease:Balance}}. To avoid any fees or potential loss of access to your unit, we kindly request that you make a payment as soon as possible. Please log in to your account at {{Location:CortexLinkPayOnline}} or give us a call at {{Location:CortexFacilityPhone}} to make a payment at your earliest convenience. Thanks!",
      voicemail:
        'Hello from {{Location:CortexFacilityName}}. {{User:FirstName|"there"}}, this is a notice that your self storage payment is past due. Your balance for unit {{Unit:Number}} is ${{Lease:Balance}}. Please log in to your account and make a payment as soon as possible to avoid late fees and restrictions. Thank you.',
      emailSubject: "Your storage payment is past due — {{Location:CortexFacilityName}}",
      emailBody: `Hi {{User:FirstName|"there"}},

Your self storage payment for unit {{Unit:Number}} is past due. Your outstanding balance is \${{Lease:Balance}}.

You can pay online any time at {{Location:CortexLinkPayOnline}}, or call us at {{Location:CortexFacilityPhone}} and we will take the payment over the phone.

Paying promptly avoids late fees and keeps your access to the unit uninterrupted. If something has come up, reply to this email or call us — we are happy to help.

Thank you,
${SIGN_OFF}`
    },
    later: {
      sms: "{{Location:CortexFacilityName}}: Your self storage payment is now {{Lease:DaysPastDue}} days past due. To avoid late fees or loss of access to your unit, please settle your outstanding balance of ${{Lease:Balance}} as soon as possible. Pay online at {{Location:CortexLinkPayOnline}} or call us at {{Location:CortexFacilityPhone}} — we're here to help if you have any questions.",
      voicemail:
        'Hello from {{Location:CortexFacilityName}}. {{User:FirstName|"there"}}, your account is now {{Lease:DaysPastDue}} days past due with a balance of ${{Lease:Balance}}. Please make a payment as soon as possible to avoid late fees or restrictions on unit {{Unit:Number}}. For assistance, call {{Location:CortexFacilityPhone}}. Thank you.',
      emailSubject: "Your storage payment is {{Lease:DaysPastDue}} days past due",
      emailBody: `Hi {{User:FirstName|"there"}},

Your self storage payment for unit {{Unit:Number}} is now {{Lease:DaysPastDue}} days past due. Your outstanding balance is \${{Lease:Balance}}. Settling it now avoids late fees and keeps your access to the unit uninterrupted.

Pay online at {{Location:CortexLinkPayOnline}}, or call {{Location:CortexFacilityPhone}} if you have questions or would like to discuss payment options.

Thank you,
${SIGN_OFF}`
    }
  },
  [BotSequenceType.MOVE_IN]: {
    guidance:
      "The tenant recently moved in; you know how many days ago. Be warm and short: welcome them or check in, confirm they have what they need (unit number, gate code, how to pay online), and offer help. Do not sell anything.",
    day0: {
      sms: "{{Location:CortexFacilityName}}: Welcome, {{User:FirstName|\"there\"}}! We're thrilled to have you with us. If you have any questions or need assistance as you settle in, please don't hesitate to ask our on-site staff or give us a call at {{Location:CortexFacilityPhone}}. Remember, your unit number is {{Unit:Number}}, and the access code for the gate is {{User:AccessCode}}.",
      voicemail:
        'Hello from {{Location:CortexFacilityName}}. {{User:FirstName|"there"}}, welcome! We\'re glad you chose us. Your unit number is {{Unit:Number}}. If you have any questions as you settle in, give us a call at {{Location:CortexFacilityPhone}}. Thank you.',
      emailSubject: "Welcome to {{Location:CortexFacilityName}}",
      emailBody: `Hi {{User:FirstName|"there"}},

Welcome to {{Location:CortexFacilityName}} — we're thrilled to have you with us.

Your unit number is {{Unit:Number}} and your gate access code is {{User:AccessCode}}. You can manage your account and payments any time at {{Location:CortexLinkAccountLogin}}.

If you have any questions or need a hand settling in, ask our on-site staff or call {{Location:CortexFacilityPhone}}.

Welcome aboard,
${SIGN_OFF}`
    },
    later: {
      sms: "{{Location:CortexFacilityName}}: It's been {{Sequence:Day}} days since your move-in, {{User:FirstName|\"there\"}}. We hope everything is to your satisfaction. If you need more space, have a question, or any feedback, we're just a message away — call {{Location:CortexFacilityPhone}}. You can manage your account and payments at {{Location:CortexLinkAccountLogin}}.",
      voicemail:
        'Hello from {{Location:CortexFacilityName}}. {{User:FirstName|"there"}}, just checking in now that you\'ve had {{Sequence:Day}} days with your unit. If you need anything or have any concerns, call us at {{Location:CortexFacilityPhone}}. Thank you.',
      emailSubject: "{{Sequence:Day}} days in — how is everything?",
      emailBody: `Hi {{User:FirstName|"there"}},

It's been {{Sequence:Day}} days since your move-in at {{Location:CortexFacilityName}}, and we hope everything is to your satisfaction. If you need more space, have a question, or any feedback, reply to this email or call {{Location:CortexFacilityPhone}}.

You can manage your account and payments any time at {{Location:CortexLinkAccountLogin}}. If you have a moment, a review helps others find us: {{Location:CortexLinkReview}}

Thank you,
${SIGN_OFF}`
    }
  },
  [BotSequenceType.MOVE_OUT]: {
    guidance:
      "The tenant is moving out or recently moved out; you know how many days ago. Thank them, make sure the move-out is complete (unit empty, lock removed), invite feedback, and let them know they're welcome back. Mention final charges or refunds only if the facts show any.",
    day0: {
      sms: "{{Location:CortexFacilityName}}: We noticed that you are moving out of our self storage facility today, and we wanted to take a moment to thank you for being a valued customer. As you move out, please let our on-site staff know if you have any questions or need assistance. In preparation for your departure, we kindly ask that you empty your unit completely and remove any personal locks. We wish you all the best and hope you'll consider us again if you ever need self storage.",
      voicemail:
        'Hello from {{Location:CortexFacilityName}}. {{User:FirstName|"there"}}, we see you\'re moving out today — thank you for being a valued customer. Please make sure your unit is completely empty and your personal lock is removed. If you have any questions, call us at {{Location:CortexFacilityPhone}}. All the best.',
      emailSubject: "Thank you — moving out of {{Location:CortexFacilityName}}",
      emailBody: `Hi {{User:FirstName|"there"}},

We noticed you're moving out of {{Location:CortexFacilityName}} today, and we wanted to thank you for being a valued customer.

Before you go, please empty your unit completely and remove any personal locks. If you have questions or need a hand, our on-site staff are happy to help, or call {{Location:CortexFacilityPhone}}.

We wish you all the best and hope you'll consider us again if you ever need storage.

${SIGN_OFF}`
    },
    later: {
      sms: "{{Location:CortexFacilityName}}: It's been {{Sequence:Day}} days since your move-out, {{User:FirstName|\"there\"}}. We hope everything concluded to your satisfaction — if there's anything we could have done better, please let us know. Should you need storage again, you can always rejoin us at {{Location:CortexLinkWebsite}}.",
      voicemail:
        "Hello from {{Location:CortexFacilityName}}. {{User:FirstName|\"there\"}}, it's been {{Sequence:Day}} days since your move-out and we hope everything concluded to your satisfaction. If there's anything we could have done better, we'd love to hear it — call {{Location:CortexFacilityPhone}}. And if you ever need storage again, we'd be glad to have you back. Thank you.",
      emailSubject: "Thank you for storing with us",
      emailBody: `Hi {{User:FirstName|"there"}},

It's been {{Sequence:Day}} days since your move-out from {{Location:CortexFacilityName}}. We hope everything concluded to your satisfaction. If there's anything we could have done better, please reply and tell us — your feedback is invaluable.

If you have a moment, we'd appreciate a review: {{Location:CortexLinkReview}}

Should you need storage in the future, you can rejoin us at {{Location:CortexLinkWebsite}} or by calling {{Location:CortexFacilityPhone}}.

All the best,
${SIGN_OFF}`
    }
  },
  [BotSequenceType.ABANDONED_CART]: {
    guidance:
      "The person started a storage reservation online and did not finish; you know how many days ago. Offer to help finish it, ask if they had a question or ran into a problem, and share the reservation link and phone number. Helpful, never pushy.",
    day0: {
      sms: "{{Location:CortexFacilityName}}: Thanks for your recent visit to our site. If you didn't complete your reservation, feel free to finish it here {{Location:CortexLinkRentUnit}} or give us a call at {{Location:CortexFacilityPhone}} today!",
      voicemail:
        "Hello, this is {{Location:CortexFacilityName}}. We wanted to check in - are you still looking for storage? If so, we'd be glad to help finalize your reservation. Just call us at {{Location:CortexFacilityPhone}} when it's convenient.",
      emailSubject: "Finish your storage reservation at {{Location:CortexFacilityName}}",
      emailBody: `Hi {{User:FirstName|"there"}},

Thanks for visiting us online. It looks like you started a storage reservation but didn't finish. Picking up where you left off takes just a minute: {{Location:CortexLinkRentUnit}}

Have a question first? Call us at {{Location:CortexFacilityPhone}} — we're glad to help.

${SIGN_OFF}`
    },
    later: {
      sms: "{{Location:CortexFacilityName}}: Hello! We noticed that you started reserving a storage unit a few days ago but didn't complete the process. Is there anything we can help with to make your decision easier? Units fill up quickly — reserve your space at {{Location:CortexLinkRentUnit}} or reach us at {{Location:CortexFacilityPhone}}.",
      voicemail:
        "Hi, this is {{Location:CortexFacilityName}}. We're still holding your storage unit, just in case. If you have any questions before finishing your reservation, give us a call at {{Location:CortexFacilityPhone}}. We're here to make this easy.",
      emailSubject: "Still need storage? We can help",
      emailBody: `Hi {{User:FirstName|"there"}},

We noticed you started reserving a storage unit with {{Location:CortexFacilityName}} but didn't complete it. Is there anything we can answer to make the decision easier — unit size, pricing, access?

Availability can change quickly. When you're ready, finish your reservation at {{Location:CortexLinkRentUnit}} or call {{Location:CortexFacilityPhone}}.

${SIGN_OFF}`
    }
  }
};

/** The days "Customize" seeds when a sequence has none (decision R4). */
export const BOT_SEQUENCE_STANDARD_DAYS: readonly number[] = [0, 3, 5];

/** The default text for every field of one day: Day 0 or after Day 0. */
export function defaultCopyFor(type: BotSequenceType, offsetDays: number): BotSequenceCopyDefaults {
  const copy = COPY[type];
  const day = offsetDays === 0 ? copy.day0 : copy.later;
  return { guidance: copy.guidance, ...day, fmsNote: BOT_SEQUENCE_FMS_NOTE_DEFAULT_TEXT };
}

/**
 * Outbound Gate decision 7: which consent category a sequence's messages carry. Abandoned cart
 * is re-engagement (`marketing`); the account sequences are `general`.
 */
export function consentCategoryForSequenceType(type: BotSequenceType): "general" | "marketing" {
  return type === BotSequenceType.ABANDONED_CART ? "marketing" : "general";
}
